// @flow

import React, { type Node, useEffect } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import {
  useUserInputAsTextFieldWithFormatting,
  useUserInputValue,
  useUserInputValues,
} from '@tectonics/prs/user-input-values';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/normalize';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getDocumentTypeFromFormattedValue } from '@tectonics/prs/customer/brazil';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import { getCurrentFeatures, usePaymentSpec } from '@tectonics/prs/payment-spec';
import type {Feature} from '@tectonics/prs/feature';
import type {Trait} from '@tectonics/prs/trait';

export function FieldBrazilianDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const paymentSpec = usePaymentSpec();
  const [userInput] = useUserInputValues();
  const currentFeatures = getCurrentFeatures(paymentSpec, userInput['selectedPaymentMethod']);

  const documentLabel = getDocumentLabelFromFeatures(currentFeatures);

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    'brazilianDocument',
    formatRaw,
    normalizeCustomerDocument,
  );

  const [,setDocumentType] = useUserInputValue('brazilianDocumentType');

  useEffect(() => {
    setDocumentType(getDocumentTypeFromFormattedValue(document));
  }, [document,  setDocumentType]);

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('brazilianDocument');

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="customer-document">
      <label htmlFor={inputId}>{documentLabel}</label>

      <TextField
        as={InputMask}
        id={inputId}
        type="tel"
        value={document || ''}
        onChange={handleChangeDocument}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        mask={maskFunction}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-document"
      />

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>Preencha o número do seu CPF</ErrorLabel>;

    case 'invalid-cpf-pattern':
    case 'invalid-cpf-number':
      return <ErrorLabel>CPF inválido</ErrorLabel>;

    case 'invalid-cnpj-pattern':
    case 'invalid-cnpj-number':
      return <ErrorLabel>CNPJ inválido</ErrorLabel>;

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>Invalid document</ErrorLabel>;
  }
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, maskRawFunction);
}

function maskFunction(value: string): ArrayMask {
  return value.length <= 14 ? CPF_MASK : CNPJ_MASK;
}

function maskRawFunction(rawValue: string): ArrayMask {
  return rawValue.length <= 11 ? CPF_MASK : CNPJ_MASK;
}

function getDocumentLabelFromFeatures(currentFeatures: ImmutableArray<Feature>): string {
  const brazilianDocumentFeature: ?Feature = currentFeatures.find((feature: Feature) => feature.name === 'field_brazilian_document');
  const traits: ImmutableArray<Trait> = brazilianDocumentFeature?.traits ?? [];

  if (traits.includes('type_cpf') && !traits.includes('type_cnpj')) {
    return 'CPF';
  }

  if (!traits.includes('type_cpf') && traits.includes('type_cnpj')) {
    return 'CNPJ';
  }

  return 'CPF ou CNPJ';
}

const CPF_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, '.',
  /[0-9]/, /[0-9]/, /[0-9]/, '.',
  /[0-9]/, /[0-9]/, /[0-9]/, '-',
  /[0-9]/, /[0-9]/,
];

const CNPJ_MASK = [
  /[0-9]/, /[0-9]/, '.',
  /[0-9]/, /[0-9]/, /[0-9]/, '.',
  /[0-9]/, /[0-9]/, /[0-9]/, '/',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-',
  /[0-9]/, /[0-9]/,
];
